import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import StudentService from '../../services/student.service';
import { useTranslation } from '../../hooks/useTranslation';

const DeletedStudentsList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null, name: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedStudents();
  }, []);

  const fetchDeletedStudents = async () => {
    setLoading(true);
    try {
      const response = await StudentService.getTrashed();
      setStudents(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted students:', error);
      showSnackbar(t('students.errorLoadingDeletedStudents') + ': ' + (error.response?.data?.message || error.message), 'error');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id, name) => {
    setRestoreDialog({ open: true, id, name });
  };

  const confirmRestore = async () => {
    try {
      await StudentService.restore(restoreDialog.id);
      showSnackbar(t('students.studentRestoredSuccess'));
      fetchDeletedStudents();
    } catch (error) {
      showSnackbar(t('students.errorRestoringStudent'), 'error');
    } finally {
      setRestoreDialog({ open: false, id: null, name: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/students')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('students.deletedStudents')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('students.searchDeletedPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('students.code')}</TableCell>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('students.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t('students.noDeletedStudentsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>
                        <Chip label={student.code} color="default" variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{formatDate(student.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('students.edit') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(student.id, student.name)}
                              title={t('students.restoreStudent')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null, name: null })}
      >
        <DialogTitle>{t('students.restoreStudentTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('students.restoreStudentConfirm', { name: restoreDialog.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null, name: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            {t('students.restoreStudent')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedStudentsList;
